import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { i18n } from '../i18n/i18n';
import { KidButton } from './kid/KidUI';
import { KidIcon } from './kid/KidIcon';
import { pictureTint, TINT_CLASS } from './kid/kidUiHelpers';
import { pinPadPress, type PinPadKey } from './kid/pinPad';

/**
 * "Who are you?" — picking a family member on a shared device, then their PIN
 * (kid-first redesign; style from mock-up phone-12-parent-gate). Big name tiles instead of a
 * native dropdown (tiny on iPad), and a large number pad. A hardware keyboard works too.
 * Members who use a password can switch to a password field.
 */

type Member = { id: string; name: string; username: string | null; is_admin: boolean; member_type: 'adult' | 'child' };

const PIN_MAX = 8;
const PIN_MIN = 4;

const MemberChooser: React.FC = () => {
  const { is_device_bound, device_members, memberLogin, deviceLogout } = useAppContext() as any;
  const { current_language } = useLanguage();
  const [selected, setSelected] = useState<Member | null>(null);
  const [pin, setPin] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Children first (they use the shared device most), then grown-ups; alphabetical within.
  const members: Member[] = useMemo(() => {
    return ((device_members || []) as Member[]).slice().sort((a, b) => {
      if (a.member_type !== b.member_type) return a.member_type === 'child' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [device_members]);

  const lockFor = (m: Member | null): { locked: boolean; until?: string } => {
    if (!m || !Array.isArray((window as any).__ds_username_locks)) return { locked: false };
    const username = m.username || m.name;
    const rec = (window as any).__ds_username_locks.find((r: any) => r.username === username);
    if (rec?.lock_until && new Date(rec.lock_until) > new Date()) return { locked: true, until: rec.lock_until };
    return { locked: false };
  };

  const lockMessage = (until?: string) =>
    until
      ? i18n.t('member_chooser.locked_until').replace('{{time}}', new Date(until).toLocaleTimeString(current_language, { hour: '2-digit', minute: '2-digit' }))
      : i18n.t('member_chooser.locked_later');

  const choose = (m: Member) => {
    setSelected(m);
    setPin('');
    setPassword('');
    setUsePassword(false);
    setError('');
  };

  const back = () => {
    setSelected(null);
    setPin('');
    setPassword('');
    setError('');
  };

  const submit = useCallback(async (secret: string) => {
    if (!selected || loading) return;
    setError('');
    setLoading(true);
    try {
      await memberLogin(selected.username || selected.name, secret);
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (/too many/i.test(msg)) setError(lockMessage());
      else if (/invalid credentials/i.test(msg)) setError(i18n.t('member_chooser.wrong_pin'));
      else setError(msg || i18n.t('member_chooser.wrong_pin'));
      setPin('');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, loading, memberLogin]);

  const press = useCallback((key: PinPadKey) => {
    if (loading) return;
    setError('');
    setPin((p) => pinPadPress(p, key, PIN_MAX));
  }, [loading]);

  // Hardware keyboard on the PIN screen: digits, Backspace, Enter, Escape.
  useEffect(() => {
    if (!selected || usePassword) return;
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key as PinPadKey);
      else if (e.key === 'Backspace') press('del');
      else if (e.key === 'Enter' && pin.length >= PIN_MIN) submit(pin);
      else if (e.key === 'Escape') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, usePassword, pin, press, submit]);

  const handleSignOutDevice = async () => {
    if (!deviceLogout || signingOut) return;
    setSigningOut(true);
    setError('');
    try {
      await deviceLogout();
    } catch (e: any) {
      setError(e?.message || 'Could not sign out');
    } finally {
      setSigningOut(false);
    }
  };

  if (!is_device_bound) return null;

  const page = 'min-h-screen bg-kid-page dark:bg-kid-bg-dark text-kid-text dark:text-kid-text-dark';

  // ── Step 1: who are you? ──
  if (!selected) {
    return (
      <div className={page} lang={current_language}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12 flex flex-col min-h-screen">
          <h1 className="font-kid-heading font-extrabold text-4xl md:text-5xl text-center mb-6 md:mb-10">
            {i18n.t('member_chooser.who_title')}
          </h1>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {members.map((m) => {
              const lock = lockFor(m);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => choose(m)}
                    className="w-full h-full min-h-[160px] md:min-h-[200px] flex flex-col items-center justify-center gap-3 p-4 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark hover:border-kid-green focus:outline-none focus-visible:ring-4 focus-visible:ring-kid-green/40 transition-colors"
                  >
                    <span
                      aria-hidden
                      className={`w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center font-kid-heading font-extrabold text-4xl md:text-5xl text-kid-text ${TINT_CLASS[pictureTint(m.name)]}`}
                    >
                      {m.name.trim().charAt(0).toLocaleUpperCase()}
                    </span>
                    <span className="font-kid-heading font-extrabold text-xl md:text-2xl leading-tight break-words hyphens-auto">{m.name}</span>
                    {m.is_admin && <KidIcon name="lock" size={22} title="Admin" />}
                    {lock.locked && <span className="text-sm font-bold text-red-700 dark:text-red-300">{lockMessage(lock.until)}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {error && <p role="alert" className="mt-6 text-center font-bold text-red-700 dark:text-red-300">{error}</p>}
          <div className="mt-auto pt-10 text-center">
            <p className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark mb-2 max-w-md mx-auto">{i18n.t('member_chooser.sign_out_device_hint')}</p>
            <button
              type="button"
              disabled={signingOut}
              onClick={handleSignOutDevice}
              className="min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300 disabled:opacity-50"
            >
              {signingOut ? '…' : i18n.t('member_chooser.sign_out_device')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: PIN (or password) ──
  const lock = lockFor(selected);
  const dots = Math.max(PIN_MIN, pin.length);
  const keys: Array<PinPadKey | null> = ['1', '2', '3', '4', '5', '6', '7', '8', '9', null, '0', 'del'];
  const keyClass =
    'h-16 md:h-20 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark font-kid-heading font-extrabold text-3xl md:text-4xl active:translate-y-[2px] hover:bg-kid-bg dark:hover:bg-kid-bg-dark disabled:opacity-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-kid-green/40';

  return (
    <div className={page} lang={current_language}>
      <div className="max-w-md mx-auto px-4 py-4 md:py-8 flex flex-col min-h-screen">
        <button
          type="button"
          onClick={back}
          aria-label={i18n.t('member_chooser.back')}
          className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold flex items-center justify-center"
        >
          <span aria-hidden>←</span>
        </button>

        <div className="flex flex-col items-center text-center mt-2">
          <span
            aria-hidden
            className={`w-20 h-20 rounded-full flex items-center justify-center font-kid-heading font-extrabold text-4xl text-kid-text ${TINT_CLASS[pictureTint(selected.name)]}`}
          >
            {selected.name.trim().charAt(0).toLocaleUpperCase()}
          </span>
          <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl mt-3">
            {i18n.t('member_chooser.hi_name').replace('{{name}}', selected.name)}
          </h1>
          <p className="text-lg font-bold mt-1">
            {usePassword ? i18n.t('member_chooser.password_label') : i18n.t('member_chooser.enter_pin')}
          </p>
        </div>

        {lock.locked && <p role="alert" className="mt-4 text-center font-bold text-red-700 dark:text-red-300">{lockMessage(lock.until)}</p>}
        {error && <p role="alert" className="mt-4 text-center font-bold text-red-700 dark:text-red-300">{error}</p>}

        {usePassword ? (
          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => { e.preventDefault(); if (password) submit(password); }}
          >
            <input
              type="password"
              autoFocus
              autoComplete="current-password"
              aria-label={i18n.t('member_chooser.password_label')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading || lock.locked}
              className="w-full min-h-[56px] px-4 rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-lg text-kid-text dark:text-kid-text-dark focus:outline-none focus:border-kid-green"
            />
            <KidButton type="submit" size="lg" fullWidth disabled={loading || lock.locked || !password}>
              {loading ? '…' : i18n.t('member_chooser.log_in')}
            </KidButton>
          </form>
        ) : (
          <>
            <div className="flex justify-center gap-3 my-6" aria-label={`${pin.length} / ${PIN_MAX}`}>
              {Array.from({ length: dots }, (_, i) => (
                <span
                  key={i}
                  className={`w-5 h-5 rounded-full border-2 border-kid-text dark:border-kid-text-dark ${i < pin.length ? 'bg-kid-text dark:bg-kid-text-dark' : ''}`}
                />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {keys.map((k, i) =>
                k === null ? (
                  <span key={i} />
                ) : (
                  <button
                    key={i}
                    type="button"
                    onClick={() => press(k)}
                    disabled={loading || lock.locked}
                    aria-label={k === 'del' ? i18n.t('member_chooser.delete_digit') : k}
                    className={`${keyClass} ${k === 'del' ? '!bg-kid-chip dark:!bg-kid-chip-dark' : ''}`}
                  >
                    {k === 'del' ? <span aria-hidden>⌫</span> : k}
                  </button>
                ),
              )}
            </div>
            <KidButton
              size="lg"
              fullWidth
              className="mt-5"
              disabled={loading || lock.locked || pin.length < PIN_MIN}
              onClick={() => submit(pin)}
            >
              {loading ? '…' : i18n.t('member_chooser.log_in')}
            </KidButton>
          </>
        )}

        <button
          type="button"
          onClick={() => { setUsePassword((v) => !v); setError(''); setPin(''); setPassword(''); }}
          className="mt-auto pt-6 mx-auto min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300"
        >
          {usePassword ? i18n.t('member_chooser.use_pin') : i18n.t('member_chooser.use_password')}
        </button>
      </div>
    </div>
  );
};

export default MemberChooser;
